import { Suspense, useContext, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { safeRedirectPath } from '../utils/redirects';
import { publicRoutes, adminProtectedRoutes, jobSeekerProtectedRoutes, recruiterProtectedRoutes } from "./AllRoutes";
import ProtectedRoute from './ProtectedRoute';
import AuthContext from '../context/AuthContext';
import MainLayout from '../layouts/MainLayout';
import NotFound from '../pages/NotFound';
import AccessDenied from '../pages/AccessDenied'; // ⬅️ make sure it's added

const AboutUs = lazy(() => import('../pages/AboutUs'));
const TermsAndConditions = lazy(() => import('../pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('../pages/PrivacyPolicy'));
const FAQs = lazy(() => import('../pages/FAQs'));
const ContactUs = lazy(() => import('../pages/ContactUs'));
// Disabled alongside the /careers route below — kept so re-enabling is one
// uncomment in each place rather than rewriting the page.
// const CareersWithUs = lazy(() => import('../pages/CareersWithUs'));

const Index = () => {
    const { isAuthenticated, userRole } = useContext(AuthContext);
    // api.js sends the user here with ?session_expired=true after a 401. This
    // page was previously bouncing anyone who still looked "authenticated"
    // (per the client-side JWT expiry check) straight back to their dashboard,
    // discarding that signal entirely. Combined with a stale token surviving
    // in sessionStorage, that turned into: dashboard 401s -> redirected to
    // /login -> instantly bounced back to dashboard -> 401s again, forever.
    // Once the user is explicitly told their session expired, always show the
    // login page rather than re-trusting a token the server just rejected.
    // Read through useLocation rather than window.location so these re-evaluate on
    // client-side navigation. window.location is not part of React's render input,
    // so a value read from it can be a navigation behind.
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const sessionExpired = params.get('session_expired') === 'true';
    const redirectParam = params.get('redirect');

    /*
     * A signed-in recruiter (or account owner) does not browse the public
     * website: every website page sends them back to their dashboard, and the
     * sidebar logo points there too. They see the website again after logging
     * out. Job seekers and admins are not affected.
     */
    const isRecruiter = isAuthenticated && (userRole?.name === 'recruiter' || userRole?.name === 'accountOwner');
    const website = (page) => (isRecruiter ? <Navigate to="/recruiter/dashboard" replace /> : <MainLayout>{page}</MainLayout>);

    return (
        <Suspense fallback={<div className="flex justify-center items-center h-screen">Loading...</div>}>
            <Routes>

                {/* Public Routes */}
                {publicRoutes.map((route) => {
                    // Only these routes should kick authenticated users back to their dashboard
                    const authOnlyRedirectPaths = ['/login', '/register', '/forgot-password'];
                    const shouldRedirectWhenAuthed = authOnlyRedirectPaths.includes(route.path) && !sessionExpired;
                    const dashboard = `/${userRole?.name === 'accountOwner' ? 'recruiter' : userRole?.name}/dashboard`;

                    /*
                     * Honour ?redirect= when bouncing a now-authenticated user off a
                     * sign-in page.
                     *
                     * This bounce used to go to the dashboard unconditionally, and it
                     * fires the instant AuthContext publishes the user — before the
                     * login page's own `navigate(redirectPath)` gets a turn. The
                     * effect was that ?redirect= never worked at all: someone who
                     * pressed Apply on a job, was sent here to sign in, and signed in
                     * successfully landed on their dashboard with the job gone. The
                     * parameter was being written by every caller and read by nobody.
                     *
                     * Jobseekers now reach sign-in without leaving the job at all (see
                     * AuthPromptDialog), so this path is the fallback: a bookmarked or
                     * emailed /login?redirect=… link, and the recruiter pages.
                     */
                    const requestedRedirect = safeRedirectPath(redirectParam);

                    return (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={
                                isAuthenticated && shouldRedirectWhenAuthed ? (
                                    <Navigate to={requestedRedirect || dashboard} replace />
                                ) : sessionExpired && authOnlyRedirectPaths.includes(route.path) ? (
                                    // The server rejected the token: sign-in must open, recruiter or not.
                                    <MainLayout>{route.element}</MainLayout>
                                ) : (
                                    website(route.element)
                                )
                            }
                        />
                    );
                })}


                {/* Admin Routes */}
                {adminProtectedRoutes.map((route) => (
                    <Route
                        key={route.path}
                        path={route.path}
                        element={
                            <ProtectedRoute isAllowed={isAuthenticated && userRole?.name === 'admin'}>
                                {route.element}
                            </ProtectedRoute>
                        }
                    />
                ))}

                {/* Recruiter Routes */}
                {recruiterProtectedRoutes.map((route) => (
                    <Route
                        key={route.path}
                        path={route.path}
                        element={
                            <ProtectedRoute isAllowed={isAuthenticated && (userRole?.name === 'recruiter' || userRole?.name === 'accountOwner')}>
                                {route.element}
                            </ProtectedRoute>
                        }
                    />
                ))}

                {/* Job Seeker Routes */}
                {jobSeekerProtectedRoutes.map((route) => (
                    <Route
                        key={route.path}
                        path={route.path}
                        element={
                            <ProtectedRoute isAllowed={isAuthenticated && userRole?.name === 'jobseeker'}>
                                {route.element}
                            </ProtectedRoute>
                        }
                    />
                ))}

                {/* Info Routes — open to everyone except a signed-in recruiter (see website()) */}
                <Route path="/about-us" element={website(<AboutUs />)} />
                <Route path="/terms-and-conditions" element={website(<TermsAndConditions />)} />
                <Route path="/privacy-policy" element={website(<PrivacyPolicy />)} />
                <Route path="/faqs" element={website(<FAQs />)} />
                <Route path="/contact-us" element={website(<ContactUs />)} />
                {/* Careers is disabled for now — its main content duplicated the
                    /jobs listing rather than covering roles at Talexo itself. The
                    page component and its route are kept here so it can be turned
                    back on once there is genuine careers content to show. */}
                {/* <Route path="/careers" element={<MainLayout><CareersWithUs /></MainLayout>} /> */}

                {/* Access Denied */}
                <Route path="/access-denied" element={<AccessDenied />} />

                {/* Not Found - Always Last */}
                <Route path="*" element={<NotFound />} />

            </Routes>
        </Suspense>
    );
};

export default Index;
